import { axios } from "@via/api/axios.tsx";
import { eventRunnerPath } from "@via/pages/projects/metadata/events/event-path";
import { ROUTES } from "@via/utils/constants.tsx";
import { isAxiosError } from "axios";
import type {
  VmetaAliasAmbiguousResponse,
  VmetaAliasResolveResponse,
} from "common";

export type AliasResolveResult =
  | { kind: "found"; target: VmetaAliasResolveResponse }
  | { kind: "ambiguous"; detail: VmetaAliasAmbiguousResponse }
  | { kind: "notFound" };

/**
 * The code as one path segment, encoded exactly once. Express decodes the parameter exactly once,
 * so the backend sees the code as typed, slashes and percent signs included.
 */
export const aliasResolveUrl = (code: string): string =>
  `/api/v1/vmeta/alias/${encodeURIComponent(code)}`;

/**
 * Asks the resolver for a normalized code. 404 and 409 are answers, not failures; anything else
 * (network, 500, an aborted request) is thrown. Callers abort a lookup they no longer want and
 * must ignore whatever it returns.
 */
export const resolveAlias = async (
  code: string,
  options: { signal?: AbortSignal } = {}
): Promise<AliasResolveResult> => {
  try {
    const res = await axios.get<VmetaAliasResolveResponse>(
      aliasResolveUrl(code),
      { signal: options.signal }
    );
    return { kind: "found", target: res.data };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return { kind: "notFound" };
    }
    if (isAxiosError(error) && error.response?.status === 409) {
      return {
        kind: "ambiguous",
        detail: error.response.data as VmetaAliasAmbiguousResponse,
      };
    }
    throw error;
  }
};

/** The record link Collections pins (spec section 2). */
export const recordDeepLink = (target: VmetaAliasResolveResponse): string => {
  const query = new URLSearchParams({
    collection: target.collectionName,
    record: target.recordId,
  });
  return `${ROUTES.SAMPLE_TRACKERS}/${encodeURIComponent(target.canvasId)}?${query.toString()}`;
};

/** Router state the record page reads to say the event form was not available (spec: one line note). */
export const EVENT_FORM_UNAVAILABLE_STATE = {
  eventFormUnavailable: true,
} as const;

export type Landing = {
  to: string;
  state?: typeof EVENT_FORM_UNAVAILABLE_STATE;
};

/**
 * Where a resolved scan opens (spec "Barcode landing"): the event form with the scanned records
 * chosen when the server named an event, otherwise the record, noting when an event form exists
 * for this code but not for this scanner.
 */
export const landingFor = (target: VmetaAliasResolveResponse): Landing => {
  if (target.event) {
    return {
      to: eventRunnerPath(target.canvasId, target.event.id, target.path),
    };
  }
  return target.eventUnavailable
    ? { to: recordDeepLink(target), state: EVENT_FORM_UNAVAILABLE_STATE }
    : { to: recordDeepLink(target) };
};

export const notFoundMessage = (code: string): string =>
  `No record found for ${code}`;

export const ambiguousMessage = (
  code: string,
  detail: VmetaAliasAmbiguousResponse
): string =>
  `${code} matches ${detail.count} records in ${detail.collectionName} (${detail.canvasName}). Ask a project owner to fix the duplicate barcodes.`;

/** What the scanner and /a/<code> say for an answer that opens no record. */
export const messageForResult = (
  result: Exclude<AliasResolveResult, { kind: "found" }>,
  code: string
): string =>
  result.kind === "notFound"
    ? notFoundMessage(code)
    : ambiguousMessage(code, result.detail);

export const lookupFailedMessage = (code: string): string =>
  `Could not look up ${code}. Check the connection and try again.`;
